import { useCallback, useEffect, useMemo, useState } from "react";
import ReactFlow, {
    Background, Controls, MiniMap, addEdge, applyEdgeChanges, applyNodeChanges, Handle, Position,
} from "reactflow";
import "reactflow/dist/style.css";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Play, Trash2, Save, Sparkles, Zap, ArrowRight } from "lucide-react";
import Modal, { Field, Input, Textarea, Select, GoldButton, GhostButton, PageHeader, EmptyState, StatusBadge } from "@/components/Ui";

/* -------- Custom React Flow node components -------- */
function TriggerNode({ data }) {
    return (
        <div className="bg-muted border border-primary px-5 py-3 min-w-[200px]">
            <div className="overline mb-1">Trigger</div>
            <div className="text-sm">{data.label || "Choose…"}</div>
            <Handle type="source" position={Position.Bottom} style={{ background: "#D4AF37", width: 8, height: 8 }} />
        </div>
    );
}
function ActionNode({ data }) {
    return (
        <div className="bg-muted border border-border px-5 py-3 min-w-[200px]">
            <Handle type="target" position={Position.Top} style={{ background: "#A3A3A3", width: 8, height: 8 }} />
            <div className="overline mb-1 !text-muted-foreground">Action</div>
            <div className="text-sm">{data.label || "Choose…"}</div>
            <Handle type="source" position={Position.Bottom} style={{ background: "#A3A3A3", width: 8, height: 8 }} />
        </div>
    );
}
const NODE_TYPES = { trigger: TriggerNode, action: ActionNode };

const NEW_FORM = { name: "", description: "", trigger: { type: "" }, actions: [], enabled: false };

export default function Automations() {
    const [items, setItems] = useState([]);
    const [catalog, setCatalog] = useState({ triggers: [], actions: [] });
    const [active, setActive] = useState(null); // automation being edited
    const [nodes, setNodes] = useState([]);
    const [edges, setEdges] = useState([]);
    const [creating, setCreating] = useState(false);
    const [newForm, setNewForm] = useState(NEW_FORM);

    const load = async () => {
        try {
            const [a, c] = await Promise.all([api.get("/automations"), api.get("/automations/catalog")]);
            setItems(a.data); setCatalog(c.data);
        } catch (e) { toast.error(apiError(e)); }
    };
    useEffect(() => { load(); }, []);

    /* Build React Flow graph from automation persisted shape */
    const openAutomation = (a) => {
        setActive(a);
        if (a.nodes && a.edges && a.nodes.length) {
            setNodes(a.nodes); setEdges(a.edges);
            return;
        }
        // Generate default vertical layout
        const tr = catalog.triggers.find(t => t.type === a.trigger?.type);
        const trigNode = {
            id: "trigger", type: "trigger", position: { x: 200, y: 40 },
            data: { label: tr?.label || "Trigger" },
        };
        const actNodes = (a.actions || []).map((ac, i) => {
            const meta = catalog.actions.find(x => x.type === ac.type);
            return { id: `act-${i}`, type: "action", position: { x: 200, y: 160 + i * 120 }, data: { label: meta?.label || ac.type } };
        });
        const edgeList = [];
        if (actNodes.length) edgeList.push({ id: "e0", source: "trigger", target: actNodes[0].id, animated: true, style: { stroke: "#D4AF37" } });
        for (let i = 1; i < actNodes.length; i++) edgeList.push({ id: `e${i}`, source: actNodes[i-1].id, target: actNodes[i].id, animated: true, style: { stroke: "#A3A3A3" } });
        setNodes([trigNode, ...actNodes]); setEdges(edgeList);
    };

    const onNodesChange = useCallback((changes) => setNodes((ns) => applyNodeChanges(changes, ns)), []);
    const onEdgesChange = useCallback((changes) => setEdges((es) => applyEdgeChanges(changes, es)), []);
    const onConnect = useCallback((p) => setEdges((es) => addEdge({ ...p, animated: true, style: { stroke: "#A3A3A3" } }, es)), []);

    const saveActive = async () => {
        if (!active) return;
        try {
            await api.patch(`/automations/${active.id}`, { nodes, edges });
            toast.success("Canvas saved");
            load();
        } catch (e) { toast.error(apiError(e)); }
    };

    const toggleEnabled = async (a) => {
        try { await api.patch(`/automations/${a.id}`, { enabled: !a.enabled }); load(); toast.success(`${a.name} ${!a.enabled ? "enabled" : "disabled"}`); }
        catch (e) { toast.error(apiError(e)); }
    };

    const deleteOne = async (id) => {
        if (!window.confirm("Delete automation?")) return;
        try { await api.delete(`/automations/${id}`); load(); if (active?.id === id) setActive(null); }
        catch (e) { toast.error(apiError(e)); }
    };

    const runTest = async (id) => {
        try { const { data } = await api.post(`/automations/${id}/run-test`); toast.success(`Preview: ${data.actions_preview.length} action(s) would fire`); }
        catch (e) { toast.error(apiError(e)); }
    };

    const createNew = async (e) => {
        e.preventDefault();
        try {
            await api.post("/automations", newForm);
            toast.success("Automation created");
            setCreating(false); setNewForm(NEW_FORM);
            load();
        } catch (e) { toast.error(apiError(e)); }
    };

    /* Add/remove actions in newForm */
    const addAction = (type) => {
        const meta = catalog.actions.find(a => a.type === type);
        const defaults = {};
        (meta?.fields || []).forEach(f => { if (f.default !== undefined) defaults[f.key] = f.default; });
        setNewForm({ ...newForm, actions: [...newForm.actions, { type, ...defaults }] });
    };
    const updateAction = (i, k, v) => {
        const a = [...newForm.actions]; a[i] = { ...a[i], [k]: v }; setNewForm({ ...newForm, actions: a });
    };
    const removeAction = (i) => setNewForm({ ...newForm, actions: newForm.actions.filter((_, j) => j !== i) });

    const triggerMeta = catalog.triggers.find(t => t.type === newForm.trigger?.type);

    return (
        <>
            <PageHeader
                title="Automation builder"
                subtitle="Drag, drop, connect. Triggers fire actions across email, WhatsApp, Telegram, SMS and tasks."
                action={<GoldButton onClick={() => setCreating(true)} data-testid="automation-new-btn"><Plus className="w-4 h-4 inline mr-2" strokeWidth={2}/>New automation</GoldButton>}
            />

            {/* Library + canvas split */}
            <div className="grid lg:grid-cols-[320px_1fr] gap-6">
                {/* Library */}
                <aside>
                    <div className="overline mb-3">Your automations</div>
                    {items.length === 0 ? (
                        <EmptyState title="None yet" subtitle="Spin up your first rule." action={<GoldButton onClick={() => setCreating(true)} data-testid="automation-empty-btn">Create</GoldButton>}/>
                    ) : (
                        <div className="space-y-2">
                            {items.map(a => (
                                <div key={a.id} className={`bg-muted border ${active?.id === a.id ? "border-primary" : "border-border"} p-4 cursor-pointer hover:border-primary transition-colors`} onClick={() => openAutomation(a)} data-testid={`automation-card-${a.id}`}>
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="min-w-0">
                                            <div className="font-serif text-sm truncate">{a.name}</div>
                                            <div className="text-xs text-muted-foreground truncate mt-1">{a.description || "—"}</div>
                                        </div>
                                        <StatusBadge status={a.enabled ? "active" : "paused"}/>
                                    </div>
                                    <div className="flex gap-2 mt-3 text-xs text-muted-foreground">
                                        <span>{(a.actions || []).length} action(s)</span>
                                        <span>·</span>
                                        <span>{a.trigger?.type?.replace(/_/g, " ") || "no trigger"}</span>
                                    </div>
                                    <div className="flex gap-3 mt-3" onClick={e => e.stopPropagation()}>
                                        <button onClick={() => toggleEnabled(a)} data-testid={`automation-toggle-${a.id}`} className="text-xs text-primary hover:underline">{a.enabled ? "Disable" : "Enable"}</button>
                                        <button onClick={() => runTest(a.id)} data-testid={`automation-test-${a.id}`} className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1"><Play className="w-3 h-3"/>Preview</button>
                                        <button onClick={() => deleteOne(a.id)} data-testid={`automation-delete-${a.id}`} className="text-xs text-muted-foreground hover:text-red-400 ml-auto"><Trash2 className="w-3 h-3"/></button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </aside>

                {/* Canvas */}
                <section>
                    {!active ? (
                        <div className="bg-muted border border-dashed border-border h-[520px] flex items-center justify-center">
                            <div className="text-center max-w-md px-6">
                                <Sparkles className="w-8 h-8 text-primary mx-auto mb-4" strokeWidth={1.5}/>
                                <h3 className="font-serif text-2xl mb-3">Pick a rule to edit, or create one.</h3>
                                <p className="text-sm text-muted-foreground mb-6">Each automation is a trigger followed by one or more actions. Drag nodes on the canvas to lay them out.</p>
                                <GoldButton onClick={() => setCreating(true)} data-testid="automation-canvas-empty-btn">Create automation</GoldButton>
                            </div>
                        </div>
                    ) : (
                        <div className="bg-background border border-border">
                            <div className="flex items-center justify-between px-5 py-3 border-b border-border flex-wrap gap-3">
                                <div className="min-w-0">
                                    <div className="font-serif text-lg truncate">{active.name}</div>
                                    <div className="text-xs text-muted-foreground truncate">{active.description}</div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <GhostButton onClick={() => runTest(active.id)} data-testid="canvas-test-btn"><Play className="w-3.5 h-3.5 inline mr-1.5" strokeWidth={1.5}/>Preview</GhostButton>
                                    <GoldButton onClick={saveActive} data-testid="canvas-save-btn"><Save className="w-3.5 h-3.5 inline mr-1.5" strokeWidth={1.5}/>Save canvas</GoldButton>
                                </div>
                            </div>
                            <div style={{ height: 540 }}>
                                <ReactFlow
                                    nodes={nodes} edges={edges}
                                    onNodesChange={onNodesChange} onEdgesChange={onEdgesChange} onConnect={onConnect}
                                    nodeTypes={NODE_TYPES} fitView proOptions={{ hideAttribution: true }}
                                    style={{ background: "#0A0A0A" }}
                                >
                                    <Background color="#2A2A2A" gap={24}/>
                                    <Controls style={{ background: "#171717", border: "1px solid #2A2A2A" }}/>
                                    <MiniMap nodeColor="#D4AF37" maskColor="rgba(10,10,10,0.85)" style={{ background: "#171717", border: "1px solid #2A2A2A" }}/>
                                </ReactFlow>
                            </div>
                        </div>
                    )}
                </section>
            </div>

            {/* Create modal */}
            <Modal open={creating} onClose={() => setCreating(false)} title="New automation" size="lg" testId="automation-modal">
                <form onSubmit={createNew} className="space-y-5">
                    <Field label="Name"><Input required value={newForm.name} onChange={e => setNewForm({ ...newForm, name: e.target.value })} placeholder="e.g. WhatsApp the client a day before hearing" data-testid="automation-name-input"/></Field>
                    <Field label="Description"><Textarea rows="2" value={newForm.description} onChange={e => setNewForm({ ...newForm, description: e.target.value })}/></Field>

                    <div className="bg-background border border-border p-4 space-y-3">
                        <div className="flex items-center gap-2"><Zap className="w-4 h-4 text-primary" strokeWidth={1.5}/><div className="overline">When</div></div>
                        <Field label="Trigger">
                            <Select required value={newForm.trigger.type} onChange={e => setNewForm({ ...newForm, trigger: { type: e.target.value } })} data-testid="automation-trigger-select"
                                options={[{ value: "", label: "— select trigger —" }, ...catalog.triggers.map(t => ({ value: t.type, label: t.label }))]}/>
                        </Field>
                        {(triggerMeta?.fields || []).map(f => (
                            <Field key={f.key} label={f.label}>
                                <Input type={f.type === "number" ? "number" : "text"} value={newForm.trigger[f.key] ?? f.default ?? ""}
                                    onChange={e => setNewForm({ ...newForm, trigger: { ...newForm.trigger, [f.key]: f.type === "number" ? Number(e.target.value) : e.target.value } })}/>
                            </Field>
                        ))}
                    </div>

                    <div className="bg-background border border-border p-4 space-y-3">
                        <div className="flex items-center gap-2"><ArrowRight className="w-4 h-4 text-primary" strokeWidth={1.5}/><div className="overline">Then</div></div>
                        {newForm.actions.map((act, i) => {
                            const meta = catalog.actions.find(a => a.type === act.type);
                            return (
                                <div key={i} className="bg-muted border border-border p-3 space-y-2">
                                    <div className="flex items-center justify-between">
                                        <div className="text-sm">{meta?.label}</div>
                                        <button type="button" onClick={() => removeAction(i)} className="text-muted-foreground hover:text-red-400 text-xs">Remove</button>
                                    </div>
                                    {(meta?.fields || []).map(f => (
                                        <Field key={f.key} label={f.label}>
                                            {f.type === "textarea" ? (
                                                <Textarea rows="2" value={act[f.key] ?? ""} onChange={e => updateAction(i, f.key, e.target.value)}/>
                                            ) : f.type === "select" ? (
                                                <Select value={act[f.key] ?? f.options[0]} onChange={e => updateAction(i, f.key, e.target.value)} options={f.options}/>
                                            ) : (
                                                <Input type={f.type === "number" ? "number" : "text"} value={act[f.key] ?? ""}
                                                    onChange={e => updateAction(i, f.key, f.type === "number" ? Number(e.target.value) : e.target.value)}/>
                                            )}
                                        </Field>
                                    ))}
                                </div>
                            );
                        })}
                        <div className="flex flex-wrap gap-2">
                            {catalog.actions.map(a => (
                                <button key={a.type} type="button" onClick={() => addAction(a.type)} data-testid={`automation-add-${a.type}`}
                                    className="text-xs border border-border hover:border-primary hover:text-primary px-3 py-1.5 transition-colors">
                                    <Plus className="w-3 h-3 inline mr-1" strokeWidth={2}/>{a.label}
                                </button>
                            ))}
                        </div>
                    </div>

                    <label className="flex items-center gap-2 text-sm text-muted-foreground">
                        <input type="checkbox" checked={newForm.enabled} onChange={e => setNewForm({ ...newForm, enabled: e.target.checked })} data-testid="automation-enabled-toggle"/>
                        Enable immediately (executor will run when wired in v1.2)
                    </label>

                    <div className="flex justify-end gap-2 pt-2">
                        <GoldButton type="submit" data-testid="automation-save-btn">Create automation</GoldButton>
                    </div>
                </form>
            </Modal>
        </>
    );
}
